import API from "../core/api";
import { getGuestAuthSettings } from "../core/utils";
import { StaticSiteDataContext } from "./StaticSiteDataContext";
import React, {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

export type VisibleGuestRsvpStatus = "yes" | "no" | "maybe";

export type VisibleGuest = {
  count: number;
  name: string;
  rsvpStatus: VisibleGuestRsvpStatus | null;
};

type VisibleGuestListContextType = {
  guests: VisibleGuest[];
  hasLoaded: boolean;
  refresh: (forceRefresh?: boolean) => void;
  invalidateVisibleGuestList: () => void;
};

const VisibleGuestListContext = createContext<VisibleGuestListContextType>({
  guests: [],
  hasLoaded: false,
  refresh: () => {},
  invalidateVisibleGuestList: () => {},
});

type VisibleGuestListProviderProps = {
  children: ReactNode;
};

const VisibleGuestListProvider = ({
  children,
}: VisibleGuestListProviderProps) => {
  const { isOnlineInvitationSite } = useContext(StaticSiteDataContext);
  const [guests, setGuests] = useState<VisibleGuest[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState(true);

  const fetchGuestList = useCallback(() => {
    if (!isOnlineInvitationSite) {
      return;
    }

    API.get("guest-management/visible-guest-list", getGuestAuthSettings()).then(
      (data) => {
        setShouldRefresh(false);
        setHasLoaded(true);
        setGuests(data);
      },
    );
  }, [isOnlineInvitationSite]);

  useEffect(() => {
    fetchGuestList();
  }, [fetchGuestList]);

  const invalidateVisibleGuestList = useCallback(() => {
    setShouldRefresh(true);
  }, []);

  const refresh = useCallback(
    (forceRefresh?: boolean) => {
      if (shouldRefresh || forceRefresh) {
        fetchGuestList();
      }
    },
    [fetchGuestList, shouldRefresh],
  );

  return (
    <VisibleGuestListContext.Provider
      value={{
        guests,
        hasLoaded,
        refresh,
        invalidateVisibleGuestList,
      }}
    >
      {children}
    </VisibleGuestListContext.Provider>
  );
};

const MemoizedVisibleGuestListProvider = memo(VisibleGuestListProvider);

/**
 * Hook to access the visible guest list data and refresh method.
 * Used in Online Invitation sites to display and refresh the guest list.
 */
const useVisibleGuestList = () => {
  return useContext(VisibleGuestListContext);
};

export {
  MemoizedVisibleGuestListProvider as VisibleGuestListProvider,
  useVisibleGuestList,
  VisibleGuestListContext,
};
export type { VisibleGuestListContextType };
